import { useState, useEffect } from "react"
import { useNavigate, Link } from "react-router-dom"
import { useDispatch, useSelector } from 'react-redux';
import { setDummyUser, clearError } from '../store/slices/authSlice';

// ── Dummy credentials ──────────────────────────────────────────
const DUMMY_USERS = [
  {
    email: "admin@honeycool.com",
    password: "admin123",
    user: {
      _id: "dummy_001",
      first_name: "Swasthika",
      last_name: "Admin",
      username: "admin@honeycool.com",
      email: "admin@honeycool.com",
      phone_number: "9999999999",
      role: [{ key: "customer", name: "Customer" }],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: "active",
      is_active: true,
    },
    token: "dummy_token_admin_001",
  },
  {
    email: "user@honeycool.com",
    password: "user123",
    user: {
      _id: "dummy_002",
      first_name: "Demo",
      last_name: "User",
      username: "user@honeycool.com",
      email: "user@honeycool.com",
      phone_number: "8888888888",
      role: [{ key: "customer", name: "Customer" }],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: "active",
      is_active: true,
    },
    token: "dummy_token_user_002",
  },
];

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, error } = useSelector((state) => state.auth);

  const [formState, setFormState] = useState({ email: "", password: "" });
  const [localError, setLocalError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  const handleChange = (e) => {
    setLocalError("");
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formState.email || !formState.password) {
      setLocalError("Please enter email and password");
      return;
    }

    setLoading(true);

    // Simulate a tiny loading delay for polish
    setTimeout(() => {
      const match = DUMMY_USERS.find(
        (u) =>
          u.email.toLowerCase() === formState.email.toLowerCase() &&
          u.password === formState.password
      );

      if (match) {
        dispatch(setDummyUser({ user: match.user, token: match.token }));
        navigate('/');
      } else {
        setLocalError("Invalid email or password. Please try again.");
      }
      setLoading(false);
    }, 600);
  };

  const displayError = localError || error;

  return (
    <div className="login-container">
      <style>
        {`
        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f7fafc;
          padding: 48px 24px;
          box-sizing: border-box;
        }
        .login-wrapper {
          width: 100%;
          max-width: 450px;
          display: flex;
          flex-direction: column;
          gap: 32px;
        }
        .login-card {
          background-color: white;
          border-radius: 0px;
          box-shadow: 0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -2px rgba(0,0,0,0.04);
          padding: 32px;
          border: 1px solid #eaeaec;
          box-sizing: border-box;
        }
        .login-title {
          font-size: 32px;
          font-weight: 800;
          margin: 0;
          color: #282c3f;
        }
        .login-subtitle {
          font-size: 15px;
          color: #696e79;
          margin: 0;
        }
        @media (max-width: 768px) {
          .login-container {
            padding: 24px 16px;
          }
          .login-wrapper {
            gap: 24px;
          }
          .login-card {
            padding: 24px 20px;
          }
          .login-title {
            font-size: 26px;
          }
          .login-subtitle {
            font-size: 14px;
          }
        }
        @media (max-width: 480px) {
          .login-container {
            padding: 16px 12px;
            background-color: white;
          }
          .login-card {
            box-shadow: none;
            border: none;
            padding: 16px 8px;
          }
          .login-title {
            font-size: 24px;
          }
        }
        `}
      </style>

      <div className="login-wrapper">
        {/* Header */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <h1 className="login-title">
            Login to your account
          </h1>
          <p className="login-subtitle">
            Access your Orders, Wishlist and Recommendations ✌️
          </p>
        </div>

        {/* Card */}
        <div className="login-card">
          {/* Demo hint */}
          <div style={{
            background: '#fff8e6',
            border: '1px solid #ffd966',
            borderRadius: '0px',
            padding: '10px 14px',
            marginBottom: '20px',
            fontSize: '12px',
            color: '#856404'
          }}>
            <strong>Demo credentials:</strong><br />
            📧 <code>admin@honeycool.com</code> / <code>admin123</code><br />
            📧 <code>user@honeycool.com</code> / <code>user123</code>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Email */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: '700', color: '#282c3f', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Email address
              </label>
              <input
                type="email"
                name="email"
                value={formState.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '0px',
                  border: displayError ? '1px solid #43c8ca' : '1px solid #e2e8f0',
                  outline: 'none',
                  boxSizing: 'border-box',
                  fontSize: '14px',
                  color: '#282c3f',
                  transition: 'border-color 0.2s'
                }}
              />
            </div>

            {/* Password */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: '700', color: '#282c3f', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formState.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '0px',
                  border: displayError ? '1px solid #43c8ca' : '1px solid #e2e8f0',
                  outline: 'none',
                  boxSizing: 'border-box',
                  fontSize: '14px',
                  color: '#282c3f',
                  transition: 'border-color 0.2s'
                }}
              />
            </div>

            {/* Error */}
            {displayError && (
              <div style={{
                color: '#43c8ca',
                fontSize: '13px',
                fontWeight: '600',
                textAlign: 'center',
                padding: '8px',
                background: '#fff0f3',
                borderRadius: '0px'
              }}>
                {displayError}
              </div>
            )}

            {/* Remember + Forgot */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" id="remember-me" style={{ cursor: 'pointer', accentColor: '#43c8ca' }} />
                <label htmlFor="remember-me" style={{ fontSize: '13px', cursor: 'pointer', color: '#696e79' }}>Remember me</label>
              </div>
              <Link to="#" style={{ fontSize: '13px', color: '#43c8ca', textDecoration: 'none', fontWeight: '600' }}>
                Forgot password?
              </Link>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: loading ? '#a0aec0' : '#43c8ca',
                color: 'white',
                border: 'none',
                borderRadius: '0px',
                fontSize: '14px',
                fontWeight: '800',
                cursor: loading ? 'not-allowed' : 'pointer',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                transition: 'background-color 0.2s'
              }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>

            {/* Sign up link */}
            <div style={{ textAlign: 'center', marginTop: '8px' }}>
              <Link to="/signup" style={{ color: '#43c8ca', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>
                New to HoneyCool? Create an account
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}